import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut, getElementAtEvent } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DoughnutChartProps } from '.';
import LoaderChart from '../../../views/dashboard_executive/LoaderChart';
import FailedChart from '../../../views/dashboard_bcld/FailedChart';
import FailedIcon from '../../../assets/icon/illustrations_empty-state_magnify.svg';
import { Box, Group, Stack, Text } from '@mantine/core';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const DoughnutChart: React.FC<DoughnutChartProps> = ({
  onClick,
  onLoad,
  data,
  chartLabels,
  chartKeys,
  chartColors,
  emptyMessage = 'No data available yet.',
  loading,
  isError,
  showPercentage = false,
  chartLabelPosition = 'top',
  chartHeight = 300,
  chartWidth = 300,
  legendColor,
  isLegendDisplay = true
}) => {
  const chartRef = useRef();
  const [datasets, setDatasets] = useState([]);

  useEffect(() => {
    if (chartLabels.length > 0 && data && data.length > 0) {
      const newDataset = {
        label: ' ',
        data: data.map((dataItem) => dataItem[chartKeys[chartKeys.length - 1]]),
        backgroundColor: chartColors
      };
      setDatasets([newDataset]);
    }
  }, [chartLabels, chartColors, chartKeys, data]);

  const handleClickChart = (event) => {
    if (!onClick) return;
    const element = getElementAtEvent(chartRef.current, event);

    onClick(element);
  };

  if (loading) {
    return <LoaderChart />;
  }

  if (isError) {
    return <FailedChart />;
  }

  if (data && data.length === 0) {
    return <FailedChart icon={FailedIcon} message={emptyMessage} />;
  }

  return (
    <Stack w={'100%'} justify="center" align="center">
      {isLegendDisplay && chartLabelPosition === 'top' && (
        <Group mx={'auto'} gap={4}>
          {chartLabels.map((label, index) => (
            <Group gap={4} key={index}>
              <div
                style={{
                  width: 15,
                  height: 15,
                  backgroundColor: chartColors[index],
                  borderRadius: 30
                }}
              />
              <Text className="text-xs-regular" c={legendColor}>
                {' '}
                {label}{' '}
              </Text>
            </Group>
          ))}
        </Group>
      )}
      <Box component="div" h={chartHeight} w={chartWidth}>
        <Doughnut
          ref={chartRef}
          onClick={handleClickChart}
          data={{
            labels:
              data &&
              data.map((item) => {
                return item.label;
              }),
            datasets
          }}
          options={{
            plugins: {
              legend: {
                display: false
              },
              datalabels: {
                backgroundColor: (context: any) => {
                  return '#fff';
                },
                borderRadius: 2,
                color: '#333',
                padding: 2,
                display: true,
                formatter: (value, context) => {
                  if (value > 0) {
                    if (showPercentage) {
                      return `${(
                        (value / datasets[0]?.data.reduce((a, b) => a + b)) *
                        100
                      ).toFixed(2)}%`;
                    }
                    return value.toLocaleString('id-ID');
                  }
                  return null;
                }
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    let label = context.dataset.label || '';

                    if (label) {
                      label += ': ';
                    }
                    if (context.parsed !== null) {
                      label += context.parsed.toLocaleString('id-ID');
                    }
                    return label;
                  },
                  title: function (context) {
                    return context[0].label || '-';
                  }
                }
              }
            }
          }}
        />
      </Box>
      {chartLabelPosition === 'bottom' && (
        <Group mx={'auto'} gap={4}>
          {chartLabels.map((label, index) => (
            <Group gap={4} key={index}>
              <div
                style={{
                  width: 15,
                  height: 15,
                  backgroundColor: chartColors[index],
                  borderRadius: 30
                }}
              />
              <Text className="text-xs-regular" c={legendColor}>
                {' '}
                {label}{' '}
              </Text>
            </Group>
          ))}
        </Group>
      )}
    </Stack>
  );
};

export default DoughnutChart;
